/**
 * Pure derivations over the action-plan tree. Kept side-effect free so they're
 * trivially unit-testable and reused across every step of the workspace.
 *
 * The DRF API exposes writes at the *action* level (select, reformulate,
 * status, dates, cost, priority) and read-only nesting above it. So issue- and
 * goal-level concepts here are derived from their actions rather than persisted
 * on the issue/goal itself.
 */
import type {
  ActionPlanAction,
  ActionPlanDetail,
  ActionPlanGoal,
  ActionPlanIssue,
  ActionPlanThematic,
} from "../api/types";

/** The name shown for an action: the user's reformulation when opted in. */
export function actionName(action: ActionPlanAction): string {
  return action.use_custom_action_name && action.custom_name
    ? action.custom_name
    : action.name;
}

/** The name shown for a goal: its custom name when opted in. */
export function goalName(goal: ActionPlanGoal): string {
  return goal.use_custom_goal_name && goal.custom_name
    ? goal.custom_name
    : goal.name;
}

/** Every action under an issue, flattened across its goals. */
export function issueActions(issue: ActionPlanIssue): ActionPlanAction[] {
  return issue.action_plan_goals.flatMap((g) => g.action_plan_actions);
}

/**
 * The actions the issue step shows and counts under an issue: catalogue ones.
 * A member's own actions and goals are neither shown nor counted there — same
 * rule as the server-rendered step.
 */
export function catalogueActions(issue: ActionPlanIssue): ActionPlanAction[] {
  return issue.action_plan_goals
    .filter((g) => !g.is_extra_goal)
    .flatMap((g) => g.action_plan_actions)
    .filter((a) => !a.is_extra_action);
}

/**
 * The issues the issue step lists: those holding at least one catalogue action.
 * An issue of custom actions only is left out, as on the server-rendered step.
 */
export function listedIssues(thematic: ActionPlanThematic): ActionPlanIssue[] {
  return thematic.action_plan_issues.filter(
    (issue) => catalogueActions(issue).length > 0,
  );
}

/** Every action in the whole plan, flattened. */
export function planActions(plan: ActionPlanDetail): ActionPlanAction[] {
  return plan.action_plan_thematics.flatMap((t) =>
    t.action_plan_issues.flatMap(issueActions),
  );
}

/**
 * An issue is "included" when at least one of its actions is selected. There's
 * no writable issue.is_selected endpoint, so selection is derived from — and
 * driven through — the actions beneath it.
 */
export function isIssueIncluded(issue: ActionPlanIssue): boolean {
  return issueActions(issue).some((a) => a.is_selected);
}

/**
 * What the issue-selection step shows on one issue row: how much structure it
 * carries, how much of it is already in the plan, and where outside help exists.
 * The resource counts are raw — the caller still gates each one on the matching
 * `*_directory_enabled` flag, since a directory the enterprise can't open
 * shouldn't be advertised.
 */
export interface IssueStats {
  goals: number;
  actions: number;
  selected: number;
  /** Actions under this issue that have at least one public expert. */
  experts: number;
  tools: number;
  aids: number;
}

export function issueStats(issue: ActionPlanIssue): IssueStats {
  const actions = catalogueActions(issue);
  return {
    goals: issue.action_plan_goals.filter(
      (g) => !g.is_extra_goal && g.action_plan_actions.some((a) => !a.is_extra_action),
    ).length,
    actions: actions.length,
    selected: actions.filter((a) => a.is_selected).length,
    experts: actions.filter((a) => a.has_public_experts).length,
    tools: actions.filter((a) => a.has_tools).length,
    aids: actions.filter((a) => a.has_financial_aids).length,
  };
}

/**
 * A copy of the plan with `patch` applied to the named actions, leaving every
 * other node untouched by identity. Backs the optimistic selection write: the
 * checkbox, the counts and the coverage bars all derive from this tree, so
 * patching it locally is what makes a click feel immediate instead of waiting
 * out the round trip.
 */
export function patchPlanActions(
  plan: ActionPlanDetail,
  actionIds: string[],
  patch: Partial<ActionPlanAction>,
): ActionPlanDetail {
  const ids = new Set(actionIds);
  return patchPlanActionsBy(plan, (action) =>
    ids.has(action.id) ? patch : undefined,
  );
}

/**
 * The same, with a per-action patch — one pass over the tree however many
 * actions change, so a reorder doesn't rebuild it once per row.
 */
export function patchPlanActionsBy(
  plan: ActionPlanDetail,
  patchFor: (action: ActionPlanAction) => Partial<ActionPlanAction> | undefined,
): ActionPlanDetail {
  return {
    ...plan,
    action_plan_thematics: plan.action_plan_thematics.map((thematic) => ({
      ...thematic,
      action_plan_issues: thematic.action_plan_issues.map((issue) => ({
        ...issue,
        action_plan_goals: issue.action_plan_goals.map((goal) => ({
          ...goal,
          action_plan_actions: goal.action_plan_actions.map((action) => {
            const patch = patchFor(action);
            return patch ? { ...action, ...patch } : action;
          }),
        })),
      })),
    })),
  };
}

/** Counts behind an objective's header on the action-selection step. */
export interface GoalStats {
  actions: number;
  selected: number;
  /** Retained actions that already name at least one stakeholder group. */
  withGroups: number;
}

export function goalStats(goal: ActionPlanGoal): GoalStats {
  const actions = goal.action_plan_actions;
  const selected = actions.filter((a) => a.is_selected);
  return {
    actions: actions.length,
    selected: selected.length,
    withGroups: selected.filter((a) => (a.consultation_groups ?? []).length > 0)
      .length,
  };
}

const groupKey = (action: ActionPlanAction) =>
  [...(action.consultation_groups ?? [])].sort().join(",");

/**
 * The stakeholder groups every retained action under an objective agrees on, or
 * `null` when they disagree (or nothing is retained). Drives the objective-level
 * picker: it shows a shared set as the objective's own, and shows "mixed"
 * rather than pretending one of the divergent sets is the truth.
 */
export function goalGroupConsensus(goal: ActionPlanGoal): string[] | null {
  const selected = goal.action_plan_actions.filter((a) => a.is_selected);
  const first = selected[0];
  if (!first) return null;
  const key = groupKey(first);
  return selected.every((a) => groupKey(a) === key)
    ? [...(first.consultation_groups ?? [])].sort()
    : null;
}

/**
 * Would cascading `groupIds` onto this objective overwrite a hand-picked
 * assignment? True only when some retained action already names groups that
 * differ from the incoming set — an action with none is filled in, not
 * overwritten, so it never triggers the warning.
 */
export function cascadeWouldOverwrite(
  goal: ActionPlanGoal,
  groupIds: string[],
): boolean {
  const next = [...groupIds].sort().join(",");
  return goal.action_plan_actions.some(
    (a) =>
      a.is_selected &&
      (a.consultation_groups ?? []).length > 0 &&
      groupKey(a) !== next,
  );
}

/**
 * Objectives carrying no retained action — the plan can't act on these. Takes
 * the thematics rather than the plan so a caller can pass the subset it shows
 * (step 2 scopes itself to the issues chosen in step 1).
 */
export function goalsMissingSelection(
  thematics: ActionPlanThematic[],
): { issueName: string; goal: ActionPlanGoal }[] {
  return thematics
    .flatMap((t) => t.action_plan_issues)
    .flatMap((issue) =>
      issue.action_plan_goals
        .filter((goal) => !goal.action_plan_actions.some((a) => a.is_selected))
        .map((goal) => ({ issueName: issue.name, goal })),
    );
}

/** Per-thematic aggregates: the coverage bar and the "n / m included" count. */
export interface ThematicStats {
  issues: number;
  includedIssues: number;
  actions: number;
  selected: number;
}

export function thematicStats(thematic: ActionPlanThematic): ThematicStats {
  const issues = listedIssues(thematic);
  const actions = issues.flatMap(catalogueActions);
  return {
    issues: issues.length,
    includedIssues: issues.filter(isIssueIncluded).length,
    actions: actions.length,
    selected: actions.filter((a) => a.is_selected).length,
  };
}

/** Whole-plan aggregates for the summary rail. */
export interface PlanCoverage {
  issues: number;
  includedIssues: number;
  actions: number;
  selected: number;
  /** Objectives with at least one selected action — what the plan actually covers. */
  goalsCovered: number;
}

export function planCoverage(plan: ActionPlanDetail): PlanCoverage {
  const issues = plan.action_plan_thematics.flatMap(listedIssues);
  const actions = issues.flatMap(catalogueActions);
  const goals = issues
    .flatMap((i) => i.action_plan_goals)
    .filter((g) => !g.is_extra_goal);
  return {
    issues: issues.length,
    includedIssues: issues.filter(isIssueIncluded).length,
    actions: actions.length,
    selected: actions.filter((a) => a.is_selected).length,
    goalsCovered: goals.filter((g) =>
      g.action_plan_actions.some((a) => !a.is_extra_action && a.is_selected),
    ).length,
  };
}

/**
 * Cut a list into two columns of roughly equal weight at a single point, so each
 * column stays contiguous and the original order still reads down-then-across.
 * The selection step lays thematic blocks out in two columns: a plain CSS
 * 2-column grid would align rows and leave a ragged gap under the shorter block,
 * and dealing blocks alternately would scramble the canonical thematic order —
 * so the cut is computed here from each block's row count.
 */
export function balanceColumns<T>(
  items: T[],
  weight: (item: T) => number,
): [T[], T[]] {
  const total = items.reduce((sum, item) => sum + weight(item), 0);
  const left: T[] = [];
  const right: T[] = [];
  let used = 0;
  for (const item of items) {
    const w = weight(item);
    // Straddle the midpoint: an item joins the left column while its own centre
    // still falls in the first half, which keeps a tall block from lopsiding it.
    if (right.length === 0 && used + w / 2 <= total / 2) {
      left.push(item);
      used += w;
    } else {
      right.push(item);
    }
  }
  return [left, right];
}

/** Selected / total action counts for a plan (drives the progress summary). */
export function planProgress(plan: ActionPlanDetail): {
  total: number;
  selected: number;
  done: number;
} {
  const actions = planActions(plan);
  const selected = actions.filter((a) => a.is_selected);
  return {
    total: actions.length,
    selected: selected.length,
    done: selected.filter((a) => a.status === "done").length,
  };
}

/** Extra (bonus) goals/actions the user added on top of the questionnaire set. */
export function extraGoals(plan: ActionPlanDetail): ActionPlanGoal[] {
  return plan.action_plan_thematics
    .flatMap((t) => t.action_plan_issues)
    .flatMap((i) => i.action_plan_goals)
    .filter((g) => g.is_extra_goal);
}

export function extraActions(plan: ActionPlanDetail): ActionPlanAction[] {
  return planActions(plan).filter((a) => a.is_extra_action);
}

/** Localized, decimal-safe rendering of a 0–5 priority score. */
export function formatPriorityScore(score: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(score);
}
